import type { UsageSheetAppData } from './types/forge-usage-sheet.ts';
import { objectKeysOfRow } from './app-object-keys.ts';
import { APP_IDENTITY_ROLES, type AppKeyRole } from './types/app-objects.ts';

/**
 * A list app (F5, mode « rows ») finds each object again by its keys (L48):
 * the name of the element at least, and the CRM ID, domain, link or e-mail the
 * source gives. Shared by the publish gate (server) and the Forge page, which
 * says it as soon as the test ends, next to « Créer l'outil ». A missing domain
 * no longer blocks anything: only a row that names nothing would be lost.
 */

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

/** The run's one result table: from the composite payload, else the legacy view. */
export const appResultTable = (payload: unknown, fallback: unknown): Record<string, unknown> | undefined => {
  const result = record(payload);
  const tables = (Array.isArray(result.tabs) ? result.tabs : []).flatMap((raw) => {
    const tab = record(raw);
    return tab.scope === 'run' && Array.isArray(tab.sections) ? tab.sections.flatMap((section) => {
      const envelope = record(record(section).envelope);
      return envelope.view_type === 'data_table' ? [record(envelope.data)] : [];
    }) : [];
  });
  const legacy = record(fallback);
  return tables.length === 1 ? tables[0] : !payload && legacy.view_type === 'data_table' ? record(legacy.data) : undefined;
};

/** Why the test cannot be published as a list, in words that say what to change; null when it can. */
export const appDataIdentityProblem = (data: UsageSheetAppData | undefined, payload: unknown, fallback: unknown): string | null => {
  if (!data || data.mode !== 'rows') return null;
  const table = appResultTable(payload, fallback);
  if (!table || !Array.isArray(table.columns) || table.columns.length === 0 || !Array.isArray(table.rows)) {
    return 'Le test doit produire le tableau de la liste, une ligne par objet.';
  }
  const nameless = table.rows.filter((row) => objectKeysOfRow(table.columns, record(row).cells, data.object).length === 0).length;
  if (!nameless) return null;
  const label = String(record(table.columns[0]).label ?? 'nom').toLowerCase();
  return `${nameless} objet${nameless > 1 ? 's' : ''} sur ${table.rows.length} du test n’${nameless > 1 ? 'ont' : 'a'} ni ${label} ni repère : la liste ne pourrait pas ${nameless > 1 ? 'les' : 'le'} retrouver.`;
};

const FOUND_BY: Partial<Record<AppKeyRole, string>> = {
  crm_id: 'l’ID CRM', domain: 'le domaine', url: 'le lien', email: 'l’e-mail', event_id: 'l’identifiant', name: 'le nom',
};

/**
 * How the test's objects will be found again (R2, L48): each row counts once,
 * by its surest key for the kind, else its name. Null when there is nothing to say.
 */
export const appObjectCoverage = (data: UsageSheetAppData | undefined, payload: unknown, fallback: unknown): string | null => {
  if (!data || data.mode !== 'rows' || !data.object) return null;
  const table = appResultTable(payload, fallback);
  if (!table || !Array.isArray(table.columns) || !Array.isArray(table.rows) || table.rows.length === 0) return null;
  const order: AppKeyRole[] = [...APP_IDENTITY_ROLES[data.object], 'name'];
  const counts = new Map<AppKeyRole, number>();
  for (const row of table.rows) {
    const roles = new Set(objectKeysOfRow(table.columns, record(row).cells, data.object).filter((key) => key.strong || key.role === 'name').map((key) => key.role));
    const role = order.find((candidate) => roles.has(candidate));
    if (role) counts.set(role, (counts.get(role) ?? 0) + 1);
  }
  const parts = order.filter((role) => counts.has(role)).map((role) => `${counts.get(role)} par ${FOUND_BY[role]}`);
  return parts.length > 0 ? `Objets du test retrouvés : ${parts.join(', ')}.` : null;
};
